import { useState, useMemo } from "react";
import { useAdmin } from "../admin-context";
import { useAppContext } from "../../../context";
import {
  BarChart, Bar, Area, ComposedChart,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";


const PIE_COLORS = ["#6366f1","#8b5cf6","#ec4899","#f59e0b","#10b981","#3b82f6","#06b6d4","#84cc16"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const alertStyles = {
  warning: { bg: "bg-amber-50 border-amber-200",    txt: "text-amber-700",   icon: "⚠️" },
  error:   { bg: "bg-red-50 border-red-200",        txt: "text-red-700",     icon: "🔴" },
  info:    { bg: "bg-blue-50 border-blue-200",       txt: "text-blue-700",    icon: "ℹ️" },
  success: { bg: "bg-emerald-50 border-emerald-200", txt: "text-emerald-700", icon: "✅" },
};

// ── Reusable UI components ─────────────────────────────────────
const fmtNum  = n => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
// fmtCurr is replaced by formatPrice from context (see Dashboard component)

const StatusBadge = ({ status }) => {
  const map = {
    delivered: "bg-emerald-100 text-emerald-700",
    shipped:   "bg-indigo-100 text-indigo-700",
    confirmed: "bg-purple-100 text-purple-700",
    pending:   "bg-amber-100 text-amber-700",
    active:    "bg-emerald-100 text-emerald-700",
    inactive:  "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${map[status?.toLowerCase()] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
};

const StatCard = ({ icon, label, value, sub, color, positive }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 ${color}`}>{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
      {sub && <p className={`text-xs mt-0.5 font-semibold ${positive === true ? "text-emerald-500" : positive === false ? "text-red-500" : "text-gray-400"}`}>{sub}</p>}
    </div>
  </div>
);

const SectionCard = ({ title, children, action, subtitle }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-start justify-between px-6 py-4 border-b border-gray-50">
      <div>
        <h2 className="text-base font-bold text-gray-800">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      <p className="font-semibold mb-1 text-gray-300">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#fff" }}>
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

// ── Dashboard ──────────────────────────────────────────────────
export default function Dashboard() {
  const { orders, products, categories } = useAdmin();
  const { formatPrice } = useAppContext();
  const fmtCurr = (n) => n >= 1000 ? `${formatPrice(n / 1000).replace(/\.00$/, "")}k` : formatPrice(n);
  const [revenueRange, setRevenueRange] = useState("12m");

  // ── Order amount helper ──────────────────────────────────────
  const getAmount = (o) =>
    Number(o.amount) ||
    (o.items || []).reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);

  // ── Summary stats ────────────────────────────────────────────
  const totalOrders    = orders.length;
  const totalRevenue   = useMemo(() => orders.reduce((s, o) => s + getAmount(o), 0), [orders]);
  const deliveredCount = useMemo(() => orders.filter(o => o.status === "delivered").length, [orders]);
  const pendingCount   = useMemo(() => orders.filter(o => o.status === "pending" || o.status === "confirmed").length, [orders]);
  const avgOrderValue  = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const deliveryRate   = totalOrders > 0 ? ((deliveredCount / totalOrders) * 100).toFixed(1) : 0;
  const lowStockCount  = useMemo(() =>
    products.filter(p => p.stock != null && p.stock <= (p.lowStockThreshold || 10)).length
  , [products]);

  // ── Monthly chart data from real orders ──────────────────────
  const allMonthlyData = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      if (!o.date) return;
      const d   = new Date(o.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!map[key]) map[key] = {
        month: MONTHS_SHORT[d.getMonth()],
        _sort: d.getFullYear() * 100 + d.getMonth(),
        orders: 0, revenue: 0,
        pending: 0, confirmed: 0, shipped: 0, delivered: 0,
      };
      map[key].orders++;
      map[key].revenue += getAmount(o);
      const s = o.status || "pending";
      if (map[key][s] !== undefined) map[key][s]++;
    });
    return Object.values(map).sort((a, b) => a._sort - b._sort);
  }, [orders]);

  const revenueData = useMemo(() => {
    if (revenueRange === "6m") return allMonthlyData.slice(-6);
    if (revenueRange === "3m") return allMonthlyData.slice(-3);
    return allMonthlyData;
  }, [allMonthlyData, revenueRange]);

  // ── Products by category (pie) ───────────────────────────────
  const byCategory = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const cat = p.category?.name || "Uncategorized";
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [products]);

  // ── Top products by price ─────────────────────────────────────
  const topProducts = useMemo(() =>
    [...products]
      .sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0))
      .slice(0, 5)
  , [products]);
  const maxPrice = topProducts[0] ? parseFloat(topProducts[0].price || 0) : 1;

  // ── Recent orders ─────────────────────────────────────────────
  const recentOrders = useMemo(() =>
    [...orders]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 7)
  , [orders]);

  // ── Low-stock alerts ──────────────────────────────────────────
  const lowStockAlerts = useMemo(() =>
    products
      .filter(p => p.stock != null && p.stock <= (p.lowStockThreshold || 10))
      .slice(0, 4)
  , [products]);

  const alerts = useMemo(() => {
    const list = [];
    lowStockAlerts.forEach(p =>
      list.push({ type: "warning", msg: `${p.name} — Stock critically low (${p.stock} units)`, time: "Live" })
    );
    if (pendingCount > 0)
      list.push({ type: "info", msg: `${pendingCount} order(s) pending or awaiting confirmation`, time: "Live" });
    if (deliveredCount > 0)
      list.push({ type: "success", msg: `${deliveredCount} order(s) successfully delivered`, time: "Live" });
    if (!list.length)
      list.push({ type: "success", msg: "All systems running normally", time: "Live" });
    return list;
  }, [lowStockAlerts, pendingCount, deliveredCount]);

  return (
    <div className="flex flex-col gap-4">

      {/* ── Row 1: Core KPIs ──────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="📦" label="Total Orders"   value={fmtNum(totalOrders)}       sub="All time"                    color="bg-indigo-500" />
        <StatCard icon="$"  label="Total Revenue"  value={fmtCurr(totalRevenue)}     sub="From delivered & all orders" color="bg-violet-500" />
        <StatCard icon="✓"  label="Delivered"      value={fmtNum(deliveredCount)}    sub={`${deliveryRate}% fulfillment rate`} positive={true} color="bg-emerald-500" />
        <StatCard icon="⏳" label="Pending"        value={fmtNum(pendingCount)}      sub="Pending + confirmed"         color="bg-amber-500" />
      </div>

      {/* ── Row 2: Product / catalog KPIs ─────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="⬡"  label="Total Products" value={fmtNum(products.length)}   sub="Live from API"               color="bg-blue-500" />
        <StatCard icon="🗂️" label="Categories"     value={fmtNum(categories.length)} sub="Active categories"           color="bg-pink-500" />
        <StatCard icon="🧾" label="Avg. Order"      value={fmtCurr(avgOrderValue)}    sub="Per order"                   color="bg-teal-500" />
        <StatCard icon="⚠"  label="Low Stock"       value={fmtNum(lowStockCount)}     sub="Below threshold"  positive={lowStockCount === 0} color="bg-red-500" />
      </div>

      {/* ── Revenue & Orders by Month ─────────────────────────── */}
      <SectionCard
        title="Revenue & Orders by Month"
        subtitle="From real order data"
        action={
          <div className="flex gap-1">
            {["3m","6m","12m"].map(r => (
              <button key={r} onClick={() => setRevenueRange(r)}
                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${revenueRange === r ? "bg-indigo-600 text-white" : "text-gray-400 hover:bg-gray-100"}`}>
                {r}
              </button>
            ))}
          </div>
        }
      >
        {revenueData.length === 0 ? (
          <p className="text-center text-gray-400 py-10 text-sm">No order data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={230}>
            <ComposedChart data={revenueData}>
              <defs>
                <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="month" tick={{fontSize:11}}/>
              <YAxis yAxisId="l" tick={{fontSize:11}} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`}/>
              <YAxis yAxisId="r" orientation="right" tick={{fontSize:11}} allowDecimals={false}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
              <Bar  yAxisId="l" dataKey="revenue" name="Revenue ($)" fill="#e0e7ff" radius={[4,4,0,0]}/>
              <Area yAxisId="r" type="monotone" dataKey="orders" name="Orders" stroke="#6366f1" strokeWidth={2.5} fill="url(#revG)" dot={{r:3,fill:"#6366f1"}}/>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      {/* ── Orders by Status + Category Pie ──────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard title="Orders by Status per Month" subtitle="Monthly fulfillment breakdown">
            {revenueData.length === 0 ? (
              <p className="text-center text-gray-400 py-10 text-sm">No order data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                  <XAxis dataKey="month" tick={{fontSize:11}}/>
                  <YAxis tick={{fontSize:11}} allowDecimals={false}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                  <Bar dataKey="pending"   name="Pending"   fill="#fbbf24" radius={[4,4,0,0]} stackId="a"/>
                  <Bar dataKey="confirmed" name="Confirmed" fill="#a78bfa" radius={[4,4,0,0]} stackId="a"/>
                  <Bar dataKey="shipped"   name="Shipped"   fill="#60a5fa" radius={[4,4,0,0]} stackId="a"/>
                  <Bar dataKey="delivered" name="Delivered" fill="#34d399" radius={[4,4,0,0]} stackId="a"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>
        </div>

        <SectionCard title="Products by Category" subtitle="Count per category">
          {byCategory.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No products yet.</p>
          ) : (
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byCategory} cx="50%" cy="50%" innerRadius={38} outerRadius={70} dataKey="value" nameKey="name" paddingAngle={3}>
                    {byCategory.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} products`, n]}/>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10}}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

{/* ── Top Products + Recent Orders ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top Products by Price" subtitle="Highest priced items">
          {topProducts.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No products yet.</p>
          ) : (
            <div className="space-y-3">
              {topProducts.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-300 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate pr-2">{p.name}</span>
                      <span className="text-sm font-bold text-gray-800 flex-shrink-0">${parseFloat(p.price || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-indigo-500" style={{width:`${(parseFloat(p.price||0)/maxPrice)*100}%`}}/>
                      </div>
                      <span className="text-xs text-gray-400 w-20 text-right">{p.category?.name || "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Recent Orders" subtitle="Latest activity">
          {recentOrders.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">No orders yet.</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(o => (
                <div key={o.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs flex-shrink-0">
                      #{o.id}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-700 truncate">
                        {o.date ? new Date(o.date).toLocaleDateString() : "—"}
                      </p>
                      <p className="text-xs text-gray-400">{(o.items||[]).length} item(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-800">{fmtCurr(getAmount(o))}</span>
                    <StatusBadge status={o.status}/>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

{/* ── Alerts ───────────────────────────────────────────── */}
      <SectionCard title="System Alerts" subtitle="Live from your data">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {alerts.map((a, i) => {
            const s = alertStyles[a.type];
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${s.bg}`}>
                <span className="text-base flex-shrink-0">{s.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-semibold ${s.txt}`}>{a.msg}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{a.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

    </div>
  );
}
